import React, { useState, useEffect } from 'react';
import '../App.css';

function Detector({ isOpen, onClose, detectorId, onDetectorDeleted, onBack}) {
    const url = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    
    const [detectorData, setDetectorData] = useState(null);
    const [logsData, setLogsData] = useState([]);

    const currentUser = localStorage.getItem('Login') || "";
    const currentRole = localStorage.getItem('role'); 
    const isAdmin = currentUser.toLowerCase() === 'admin@deadergate' || currentRole === 'admin';
    
    const [tunnelPortCreated, setTunnelPortCreated] = useState('');
    const [changedAlias, setChangedAlias] = useState('');
    const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
    const [isLoading, setIsLoading] = useState(true);

    const [spoofConfig, setSpoofConfig] = useState({
        wifiEnable: true, wifiCount: 10,
        ridEnable: true, ridCount: 10,
        ocuEnable: true, ocuCount: 10,
        minRadius: 50, maxRadius: 500
    });
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);

    const isDeviceOnline = (device) => {
        if (!device || !device.lastConnection || !device.module_status) return false;
        const diffInSeconds = (new Date() - new Date(device.lastConnection)) / 1000;
        return diffInSeconds < 30;
    };

    const showMessage = (text, type = "success") => {
        setStatusMsg({ text, type });
        setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    };

    const formatTacticalDate = (isoString) => {
        if (!isoString) return "---";
        const date = new Date(isoString);
        if (isNaN(date)) return "---";
        const day = (0 + date.getDate().toString()).slice(-2);
        const month = (0 + (date.getMonth() + 1).toString()).slice(-2);
        const year = date.getFullYear().toString().slice(-2);
        const hours = (0 + date.getHours().toString()).slice(-2);
        const minutes = (0 + date.getMinutes().toString()).slice(-2);
        const seconds = (0 + date.getSeconds().toString()).slice(-2);
        return `${day}/${month}/${year} [${hours}:${minutes}:${seconds}]`;
    };

    // =========================================================
    // MOTOR 1: DATOS VIVOS Y LOGS (Refresco constante)
    // =========================================================
    const fetchDetectorDetails = async () => {
        if (!isOpen || !detectorId) return;
        try {
            let options = {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'x-access-token': token }
            };
            
            let response = await fetch(url + '/detector/' + detectorId, options);
            if (response.ok) {
                const dataReceived = await response.json();
                setDetectorData(dataReceived);
                if (!changedAlias && dataReceived.alias) setChangedAlias(dataReceived.alias);
            }

            let logResponse = await fetch(url + '/logList/' + detectorId, options);
            if (logResponse.ok) {
                let logData = await logResponse.json();
                logData.reverse(); 
                setLogsData(logData.slice(0, 15)); 
            }
        } catch (err) {
            console.error("Error obteniendo detalles:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // =========================================================
    // MOTOR 2: CONFIGURACIÓN ESTÁTICA (Solo al abrir)
    // =========================================================
    const fetchConfig = async () => {
        if (!isOpen || !detectorId || !isAdmin) return;
        try {
            let options = {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'x-access-token': token }
            };
            let resConfig = await fetch(url + '/config/' + detectorId, options);
            if (resConfig.ok) {
                let configData = await resConfig.json();
                // Asegurarnos de que no machacamos el estado si el usuario ya está editando
                if (configData && !hasUserEdited && !isSavingConfig && Object.keys(configData).length > 0) {
                    setSpoofConfig({
                        // 👇 CORRECCIÓN: Leemos directamente de configData, no de configData.config
                        wifiEnable: configData.wifiDroneIdEnable === "true",
                        wifiCount: parseInt(configData.wifiDroneCount) || 10,
                        ridEnable: configData.remoteIdEnable === "true",
                        ridCount: parseInt(configData.remoteIdCount) || 10,
                        ocuEnable: configData.ocuSyncEnable === "true",
                        ocuCount: parseInt(configData.ocuSyncCount) || 10,
                        minRadius: parseInt(configData.gpsMinRadius) || 0,
                        maxRadius: parseInt(configData.gpsMaxRadius) || 5000
                    });
                }
            }
        } catch (err) {
            console.error("Error obteniendo config:", err);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setHasUserEdited(false); // Reset al abrir
            fetchDetectorDetails();
            fetchConfig(); // Lanzamos la petición de config solo una vez
        }
        const timer = setInterval(() => {
            if (isOpen) fetchDetectorDetails();
        }, 7000);
        return () => clearInterval(timer);
    }, [isOpen, detectorId]); 

    const handleConfigChange = (key, value) => {
        setHasUserEdited(true);
        setSpoofConfig(prev => ({ ...prev, [key]: value }));
    };

    // ==========================================
    // ACCIONES TÁCTICAS
    // ==========================================
    const executeAction = async (actionPath, actionPayload, successMsg) => {
        try {
            let options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': token },
                body: JSON.stringify(actionPayload)
            };
            let response = await fetch(url + actionPath, options);
            if (response.ok) {
                showMessage(successMsg);
                fetchDetectorDetails();
                return true;
            } else {
                showMessage("Error ejecutando la orden.", "error");
                return false;
            }
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const powerOffDetector = () => {
        let popup = window.prompt(`⚠️ REMOTO SHUTDOWN\Writte down 'shutdown' to proceed to shutdown the device ${detectorData?.alias || detectorId}:`);
        if (popup === 'shutdown') {
            executeAction('/action', { type: 'action', ident: detectorId, payload: { target: 'system', action: 'shutdown', params: '', user: localStorage.getItem("Login") } }, "🟢 Shutdown order sent.");
        }
    };

    const restartDetector = () => {
        if (window.confirm(`Restart the device ${detectorData?.alias || detectorId}?`)) {
            executeAction('/action', { type: 'action', ident: detectorId, payload: { target: 'system', action: 'reboot', params: '', user: localStorage.getItem("Login") } }, "🟢 Restart order sent.");
        }
    };

    const updateDetector = () => {
        let version = window.prompt(`☁️ FIRMWARE UPDATE\nEnter the version you want to update to (e.g., v2.1.0):`);
        if (version) {
            executeAction('/action', { type: 'action', ident: detectorId, payload: { target: 'system', action: 'update', params: version, user: localStorage.getItem("Login") } }, `🟢 Update order to ${version} sent.`);
        }
    };

    const deleteDetector = async () => {
        let popup = window.prompt(`⚠️ DELETE DEVICE\nWrite 'delete' to remove ${detectorData?.alias || detectorId} from the database:`);
        if (popup === 'delete') {
            const success = await executeAction('/delete', { email: localStorage.getItem("Login"), ident: detectorId }, "🗑️ Device deleted.");
            if (success) { onClose(); if(onDetectorDeleted) onDetectorDeleted(); }
        }
    };

    const createNewAlias = (e) => {
        e.preventDefault();
        executeAction('/detector/' + detectorId, { action: 'update', ident: detectorId, payload: { alias: changedAlias } }, "✅ Alias actualizado.");
        fetch(url + '/action', { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-access-token': token }, body: JSON.stringify({ type: 'action', ident: detectorId, payload: { target: 'alias', action: "change", params: changedAlias, user: localStorage.getItem("Login") } }) });
    };

    const createTunnelConnection = (e) => {
        e.preventDefault();
        if(!tunnelPortCreated) return;
        executeAction('/action', { type: 'action', ident: detectorId, payload: { target: 'tunnel', action: 'create', params: tunnelPortCreated, user: localStorage.getItem("Login") } }, `✅ Creating tunnel on port ${tunnelPortCreated}...`);
        setTunnelPortCreated('');
    };

    const deleteTunnelConnection = (port) => {
        if(window.confirm(`Close the tunnel on port ${port}?`)) {
            executeAction('/action', { type: 'action', ident: detectorId, payload: { target: 'tunnel', action: 'delete', params: String(port), user: localStorage.getItem("Login") } }, "🗑️ Deleted tunnel.");
        }
    };

    const saveSpoofingConfig = async () => {
        setIsSavingConfig(true);
        try {
            const configPayload = {
                wifiDroneIdEnable: spoofConfig.wifiEnable ? "true" : "false",
                wifiDroneCount: spoofConfig.wifiCount.toString(),
                remoteIdEnable: spoofConfig.ridEnable ? "true" : "false",
                remoteIdCount: spoofConfig.ridCount.toString(),
                ocuSyncEnable: spoofConfig.ocuEnable ? "true" : "false",
                ocuSyncCount: spoofConfig.ocuCount.toString(),
                gpsMinRadius: spoofConfig.minRadius.toString(),
                gpsMaxRadius: spoofConfig.maxRadius.toString()
            };

            await fetch(url + '/config/' + detectorId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': token },
                body: JSON.stringify(configPayload)
            });
            showMessage("✅ Configuration saved correctly.");
            setHasUserEdited(false); // Volvemos a permitir que se refresque visualmente
        } catch(e) {
            showMessage("❌ Network error.", "error");
        }
        setIsSavingConfig(false);
    };

    if (!isOpen) return null;

    // Asegurarnos de que leemos correctamente el array de túneles (tu Backend lo guarda en 'tunnels' o 'connections' dependiendo de cómo lo pasas en 'handleStatusMsg')
    // Según el JSON y tu backend, lo guardas en `connections: statusMsg.tunnels`
    const tunnelsList = detectorData?.tunnels || detectorData?.connections || [];

    return (
        <div className={`alerts-page-wrapper ${isOpen ? 'open' : ''}`}>
            
            {/* CABECERA */}
            <div className="alerts-header" style={{ borderBottom: 'none', marginBottom: '0' }}>
                <h1 className="alerts-title" style={{fontSize: '16px', color: '#888'}}>
                    <i className="fa-solid fa-satellite-dish" style={{marginRight: '10px'}}></i> 
                    DEVICE MANAGEMENT
                </h1>
                <button className="btn-close-alerts" onClick={onBack}>
                    <i className="fa-solid fa-arrow-left"></i>
                </button>
                <button className="btn-close-alerts" onClick={onClose} title="Cerrar panel">
                    <i className="fa-solid fa-xmark"></i>
                </button>
            </div>

            {statusMsg.text && (
                <div style={{ padding: '10px', borderRadius: '8px', marginBottom: '15px', backgroundColor: statusMsg.type === 'error' ? 'rgba(255,0,0,0.2)' : 'rgba(0,255,0,0.2)', color: statusMsg.type === 'error' ? '#ff4444' : '#00ff00', border: `1px solid ${statusMsg.type === 'error' ? '#ff4444' : '#00ff00'}`, fontSize: '13px', textAlign: 'center', fontWeight: 'bold' }}>
                    {statusMsg.text}
                </div>
            )}

            {isLoading || !detectorData ? (
                <div style={{ textAlign: 'center', color: '#888', marginTop: '50px' }}>CONNECTING ...</div>
            ) : (
                <div className="logs-grid">
                    
                    <div className="asset-hero-header">
                        <div className="asset-hero-sn">{detectorData.ident || detectorId}</div>
                        <div className="asset-hero-alias">{detectorData.alias || "No Alias"}</div>
                    </div>

                    {/* TELEMETRÍA GENERAL */}
                    <div className="telemetry-section">
                        <div className="register-section-title" style={{marginTop: 0}}>DEVICE STATUS INFO </div>
                        
                        <div className="telemetry-row">
                            <span className="telemetry-label">Status</span>
                            <span className="telemetry-value">
                                {isDeviceOnline(detectorData) 
                                    ? <span style={{color: '#00ff00', textShadow: '0 0 5px #00ff00'}}>🟢 ONLINE</span> 
                                    : <span style={{color: '#ff3333'}}>🔴 OFFLINE</span>
                                }
                            </span>
                        </div>
                        <div className="telemetry-row">
                            <span className="telemetry-label">Mode</span>
                            <span className="telemetry-value">{detectorData.operationMode || "CUSTOM"}</span>
                        </div>
                        <div className="telemetry-row">
                            <span className="telemetry-label">OS Version</span>
                            <span className="telemetry-value">{detectorData.version || "---"}</span>
                        </div>
                        <div className="telemetry-row">
                            <span className="telemetry-label">Location (Lat/Lon)</span>
                            <span className="telemetry-value">
                                {detectorData.location ? `${detectorData.location.lat.toFixed(4)}, ${detectorData.location.lon.toFixed(4)}` : "---"}
                            </span>
                        </div>
                        <div className="telemetry-row">
                            <span className="telemetry-label">Last Ping</span>
                            <span className="telemetry-value">{formatTacticalDate(detectorData.lastConnection)}</span>
                        </div>
                    </div>

                    {/* CONFIGURACIÓN DE SPOOFING */}
                    {isAdmin && (
                        <div className="telemetry-section">
                            <div className="register-section-title" style={{marginTop: 0}}>SPOOFING CONFIGURATION</div>
                            
                            <div className="spoofing-config-block">
                                <div className="config-row">
                                    <label>Wi-Fi Module</label>
                                    <input type="checkbox" className="tactical-toggle" 
                                        checked={spoofConfig.wifiEnable} 
                                        onChange={(e) => handleConfigChange('wifiEnable', e.target.checked)} 
                                    />
                                </div>
                                {spoofConfig.wifiEnable && (
                                    <div>
                                        <div style={{fontSize:'11px', color:'#aaa', display:'flex', justifyContent:'space-between'}}>
                                            <span>Drone Count</span>
                                            <span style={{color:'#fff', fontWeight:'bold'}}>{spoofConfig.wifiCount}</span>
                                        </div>
                                        <input type="range" className="tactical-slider" min="1" max="100" 
                                            value={spoofConfig.wifiCount} 
                                            onChange={(e) => handleConfigChange('wifiCount', parseInt(e.target.value))} 
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="spoofing-config-block">
                                <div className="config-row">
                                    <label>Remote ID Module</label>
                                    <input type="checkbox" className="tactical-toggle" 
                                        checked={spoofConfig.ridEnable} 
                                        onChange={(e) => handleConfigChange('ridEnable', e.target.checked)} 
                                    />
                                </div>
                                {spoofConfig.ridEnable && (
                                    <div>
                                        <div style={{fontSize:'11px', color:'#aaa', display:'flex', justifyContent:'space-between'}}>
                                            <span>Drone Count</span>
                                            <span style={{color:'#fff', fontWeight:'bold'}}>{spoofConfig.ridCount}</span>
                                        </div>
                                        <input type="range" className="tactical-slider" min="1" max="100" 
                                            value={spoofConfig.ridCount} 
                                            onChange={(e) => handleConfigChange('ridCount', parseInt(e.target.value))} 
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="spoofing-config-block">
                                <div className="config-row">
                                    <label>OcuSync Module</label>
                                    <input type="checkbox" className="tactical-toggle" 
                                        checked={spoofConfig.ocuEnable} 
                                        onChange={(e) => handleConfigChange('ocuEnable', e.target.checked)} 
                                    />
                                </div>
                                {spoofConfig.ocuEnable && (
                                    <div>
                                        <div style={{fontSize:'11px', color:'#aaa', display:'flex', justifyContent:'space-between'}}>
                                            <span>Drone Count</span>
                                            <span style={{color:'#fff', fontWeight:'bold'}}>{spoofConfig.ocuCount}</span>
                                        </div>
                                        <input type="range" className="tactical-slider" min="1" max="100" 
                                            value={spoofConfig.ocuCount} 
                                            onChange={(e) => handleConfigChange('ocuCount', parseInt(e.target.value))} 
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="spoofing-config-block">
                                <div style={{fontSize:'11px', color:'#aaa', marginBottom:'10px'}}>OPERATION RADIUS (Meters)</div>
                                
                                <div style={{marginBottom:'10px'}}>
                                    <div style={{display:'flex', justifyContent:'space-between', fontSize:'11px'}}>
                                        <span>Min Radius</span><span style={{color:'#fff'}}>{spoofConfig.minRadius}m</span>
                                    </div>
                                    <input type="range" className="tactical-slider" min="0" max="1000" step="10"
                                        value={spoofConfig.minRadius} 
                                        onChange={(e) => handleConfigChange('minRadius', parseInt(e.target.value))} 
                                    />
                                </div>

                                <div>
                                    <div style={{display:'flex', justifyContent:'space-between', fontSize:'11px'}}>
                                        <span>Max Radius</span><span style={{color:'#fff'}}>{spoofConfig.maxRadius}m</span>
                                    </div>
                                    <input type="range" className="tactical-slider" min="50" max="5000" step="50"
                                        value={spoofConfig.maxRadius} 
                                        onChange={(e) => handleConfigChange('maxRadius', parseInt(e.target.value))} 
                                    />
                                </div>
                            </div>

                            <button className="btn-register" style={{width:'100%', padding:'12px', background: isSavingConfig ? '#666' : '#00bcd4', color: isSavingConfig ? '#ccc' : '#000', border:'none'}} 
                                onClick={saveSpoofingConfig} disabled={isSavingConfig || !hasUserEdited}>
                                {isSavingConfig ? 'SAVING...' : (hasUserEdited ? 'SYNC CONFIG TO DEVICE' : 'CONFIG UP TO DATE')}
                            </button>
                        </div>
                    )}

                    {/* ALIAS */}
                    {isAdmin && (
                        <div className="telemetry-section">
                            <div className="register-section-title" style={{marginTop: 0}}>CONFIGURATION</div>
                            <form className="tactical-input-group" onSubmit={createNewAlias} style={{marginBottom: 0}}>
                                <input type="text" placeholder="New Alias..." value={changedAlias} onChange={(e) => setChangedAlias(e.target.value)} />
                                <button type="submit" className="btn-register">SAVE</button>
                            </form>
                        </div>
                    )}

                    {/* 👇 TÚNELES CORREGIDOS */}
                    {isAdmin && (
                        <div className="telemetry-section">
                            <div className="register-section-title" style={{marginTop: 0}}>ACTIVE TUNNELS</div>
                            
                            {tunnelsList && tunnelsList.length > 0 ? (
                                tunnelsList.map((tunnel, idx) => (
                                    <div key={idx} className="tunnel-item">
                                        <div className="tunnel-info">
                                            <strong>{tunnel.tunnelName || "Tunnel"}</strong>
                                            <span>Local: {tunnel.tunnelLocalPort} | Remote: {tunnel.tunnelRemote}:{tunnel.tunnelPort}</span>
                                        </div>
                                        <button className="btn-action-icon deny" onClick={() => deleteTunnelConnection(tunnel.tunnelPort)}>
                                            <i className="fa-solid fa-trash"></i>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div style={{textAlign: 'center', fontSize: '12px', color: '#666', marginBottom: '15px'}}>No hay túneles activos.</div>
                            )}

                            <form className="tactical-input-group" onSubmit={createTunnelConnection} style={{marginBottom: 0, marginTop: '10px'}}>
                                <input type="number" placeholder="Local port..." value={tunnelPortCreated} onChange={(e) => setTunnelPortCreated(e.target.value)} />
                                <button type="submit" className="btn-register" style={{background: 'rgba(0,188,212,0.2)', color: '#00bcd4', borderColor: '#00bcd4'}}>OPEN</button>
                            </form>
                        </div>
                    )}

                    {/* ACTIONS */}
                    <div className="register-section-title" style={{marginTop: '10px'}}>ACTIONS</div>
                    <div className="action-grid" style={{ marginBottom: '40px' }}>
                        <div className="btn-tactical-action warning" onClick={restartDetector}><i className="fa-solid fa-rotate-right"></i> RESTART</div>
                        <div className="btn-tactical-action danger" onClick={powerOffDetector}><i className="fa-solid fa-power-off"></i> SHUTDOWN</div>
                        <div className="btn-tactical-action info" onClick={updateDetector}><i className="fa-solid fa-cloud-arrow-up"></i> UPDATE</div>
                        <div className="btn-tactical-action danger" onClick={deleteDetector} style={{ borderStyle: 'dashed' }}><i className="fa-solid fa-trash"></i> DELETE</div>
                    </div>

                    {/* LOGS */}
                    <div>
                        <div className="register-section-title">RECENT ALERTS</div>
                        {logsData.length === 0 ? (
                            <div style={{textAlign: 'center', fontSize: '12px', color: '#666', marginBottom: '20px'}}>Sin eventos recientes.</div>
                        ) : (
                            logsData.map((log, idx) => (
                                <div key={idx} className={`log-card mini-log-card level-${log.level}`} style={{marginBottom: '8px'}}>
                                    <div className="log-card-header">
                                        <span className="log-date">{formatTacticalDate(log.dateTime)}</span>
                                        <span className={`log-badge ${log.level}`}>{log.level}</span>
                                    </div>
                                    <div className="log-message">{log.message}</div>
                                </div>
                            ))
                        )}
                    </div>

                </div>
            )}
        </div>
    );
}

export default Detector;